import { createFeatureSelector, createSelector } from '@ngrx/store';
import { find } from 'lodash';

import { FolderRouterSelectors } from '@lockton-apps/shared/data-access/router';
import { FolderHelpers } from '@lockton-apps/shared/util/helpers';

import {
  FOLDERS_FEATURE_KEY,
  FoldersState,
  foldersAdapter,
} from './folders.reducer';

export const selectFoldersState =
  createFeatureSelector<FoldersState>(FOLDERS_FEATURE_KEY);

const { selectAll, selectEntities } = foldersAdapter.getSelectors();

export const selectFoldersLoaded = createSelector(
  selectFoldersState,
  (state: FoldersState) => state.loaded
);

export const selectFoldersError = createSelector(
  selectFoldersState,
  (state: FoldersState) => state.error
);

export const selectAllFolders = createSelector(
  selectFoldersState,
  (state: FoldersState) => selectAll(state)
);

export const selectFoldersEntities = createSelector(
  selectFoldersState,
  (state: FoldersState) => selectEntities(state)
);

export const selectSelectedId = createSelector(
  selectFoldersState,
  (state: FoldersState) => state.selectedId
);

export const selectEntity = createSelector(
  selectFoldersEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const selectRoutedFolder = createSelector(
  selectFoldersEntities,
  FolderRouterSelectors.selectRoutedId,
  (entities, routedId) => (routedId ? entities[routedId] : null)
);

export const getFoldersInSelected = createSelector(
  selectAllFolders,
  selectSelectedId,
  (folders, selectedId) => {
    if (!selectedId) {
      return folders.filter((folder) => !folder.parentFolderId);
    }

    return folders.filter((folder) => folder.parentFolderId === selectedId);
  }
);

export const selectRoutedFolders = createSelector(
  selectAllFolders,
  FolderRouterSelectors.selectRoutedId,
  (folders, routedId) => {
    if (!routedId) {
      return folders.filter((folder) => !folder.parentFolderId);
    }

    return folders.filter((folder) => folder.parentFolderId === routedId);
  }
);

export const getSelectedParent = createSelector(
  selectAllFolders,
  selectEntity,
  (folders, selectedFolder) =>
    selectedFolder
      ? find(folders, (folder) => folder.id === selectedFolder.parentFolderId)
      : undefined
);

export const selectRoutedParent = createSelector(
  selectAllFolders,
  selectRoutedFolder,
  (folders, routedFolder) =>
    routedFolder
      ? find(folders, (folder) => folder.id === routedFolder.parentFolderId)
      : undefined
);

export const selectRoutedPath = createSelector(
  selectAllFolders,
  selectRoutedFolder,
  (folders, routedFolder) =>
    routedFolder?.id ? FolderHelpers.folderPath(folders, routedFolder?.id) : []
);
